import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * App-wide transient notifications.
 *
 * Two pages had grown their own toast and the rest had none, so most failure
 * paths only reached `console.error`. That is worse than an ugly error: a delete
 * or a status change that fails silently is indistinguishable from a button that
 * does nothing, which is exactly what users reported.
 *
 * Rule of thumb: if a click hit the network, the user must learn whether it
 * worked. `useToast().error(...)` in every catch block.
 */

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastApi {
  show: (message: string, variant?: ToastVariant, durationMs?: number) => void;
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
  warning: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success:
    'bg-green-50 text-green-800 border-green-200 '
    + 'dark:bg-green-900/60 dark:text-green-200 dark:border-green-700',
  error:
    'bg-red-50 text-red-800 border-red-200 '
    + 'dark:bg-red-900/60 dark:text-red-200 dark:border-red-700',
  warning:
    'bg-amber-50 text-amber-900 border-amber-300 '
    + 'dark:bg-amber-900/60 dark:text-amber-200 dark:border-amber-700',
  info:
    'bg-stone-50 text-stone-800 border-stone-200 '
    + 'dark:bg-stone-800 dark:text-stone-200 dark:border-stone-700',
};

const VARIANT_ICON: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 shrink-0" />,
  error: <XCircle className="w-5 h-5 shrink-0" />,
  warning: <AlertTriangle className="w-5 h-5 shrink-0" />,
  info: <Info className="w-5 h-5 shrink-0" />,
};

// Errors stay longer — they usually carry something the user needs to read.
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  error: 8000,
  warning: 6000,
  info: 4000,
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message: string, variant: ToastVariant = 'info', durationMs?: number) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, message, variant }]);
      const timer = window.setTimeout(
        () => dismiss(id),
        durationMs ?? DEFAULT_DURATION[variant]
      );
      timers.current.set(id, timer);
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m, d) => show(m, 'success', d),
      error: (m, d) => show(m, 'error', d),
      warning: (m, d) => show(m, 'warning', d),
      info: (m, d) => show(m, 'info', d),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {ReactDOM.createPortal(
        <div
          // Above modals: a toast reporting that the modal's save failed has to be
          // readable while the modal is still open.
          className="fixed top-4 right-4 z-[1100] flex flex-col gap-2 pointer-events-none print:hidden"
          role="region"
          aria-label="Notifications"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role="status"
              aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
              className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-sm animate-fadeInUp ${VARIANT_CLASS[toast.variant]}`}
            >
              {VARIANT_ICON[toast.variant]}
              <span className="font-medium text-sm">{toast.message}</span>
              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
                className="ml-auto -mr-1 p-0.5 rounded opacity-60 hover:opacity-100 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

/**
 * Returns the toast API.
 *
 * Falls back to console logging when no provider is mounted rather than throwing,
 * so a component rendered outside the app shell (tests, isolated previews) does
 * not crash on a notification.
 */
export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  return useMemo<ToastApi>(() => {
    if (ctx) return ctx;
    const fallback = (message: string, variant: ToastVariant = 'info') =>
      // eslint-disable-next-line no-console
      console[variant === 'error' ? 'error' : 'log'](`[toast:${variant}] ${message}`);
    return {
      show: fallback,
      success: (m) => fallback(m, 'success'),
      error: (m) => fallback(m, 'error'),
      warning: (m) => fallback(m, 'warning'),
      info: (m) => fallback(m, 'info'),
      dismiss: () => {},
    };
  }, [ctx]);
};

export default ToastProvider;
